import React, { createContext, useContext, useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';

interface Settings {
  general: {
    appName: string;
    supportEmail: string;
    timezone: string;
    dateFormat: string;
    maintenanceMode: boolean;
    allowProfessionalRegistrations: boolean;
  };
  security: {
    sessionTimeout: number;
    maxLoginAttempts: number;
    require2FA: boolean;
    passwordMinLength: number;
    passwordComplexity: 'low' | 'medium' | 'high';
    passwordExpiration: boolean;
    advancedLogging: boolean;
    logRetentionDays: number;
  };
  notifications: {
    emailNewProfessionals: boolean;
    emailNewBookings: boolean;
    emailContactMessages: boolean;
    alertCriticalErrors: boolean;
    alertPerformance: boolean;
    alertDailyReports: boolean;
    welcomeTemplate: string;
    bookingTemplate: string;
  };
  integrations: {
    stripe: {
      enabled: boolean;
      testMode: boolean;
      apiKey: string;
      webhookUrl: string;
    };
    supabase: {
      url: string;
      apiKey: string;
    };
    resend: {
      enabled: boolean;
      testMode: boolean;
      apiKey: string;
      senderEmail: string;
    };
  };
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (section: keyof Settings, values: Partial<Settings[keyof Settings]>) => Promise<void>;
  isLoading: boolean;
}

const defaultSettings: Settings = {
  general: {
    appName: 'FullBook',
    supportEmail: 'support@fullbook.com',
    timezone: 'Europe/Paris',
    dateFormat: 'DD/MM/YYYY',
    maintenanceMode: false,
    allowProfessionalRegistrations: true,
  },
  security: {
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    require2FA: true,
    passwordMinLength: 8,
    passwordComplexity: 'medium',
    passwordExpiration: false,
    advancedLogging: true,
    logRetentionDays: 30,
  },
  notifications: {
    emailNewProfessionals: true,
    emailNewBookings: true,
    emailContactMessages: true,
    alertCriticalErrors: true,
    alertPerformance: false,
    alertDailyReports: true,
    welcomeTemplate: 'Bienvenue sur FullBook !',
    bookingTemplate: 'Votre réservation a été confirmée.',
  },
  integrations: {
    stripe: {
      enabled: true,
      testMode: true,
      apiKey: '',
      webhookUrl: 'https://api.fullbook.com/webhooks/stripe',
    },
    supabase: {
      url: import.meta.env.VITE_SUPABASE_URL || '',
      apiKey: import.meta.env.VITE_SUPABASE_ANON_KEY || '',
    },
    resend: {
      enabled: true,
      testMode: true,
      apiKey: '',
      senderEmail: 'no-reply@fullbook.com',
    },
  },
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const updateSettings = useCallback(async (
    section: keyof Settings,
    values: Partial<Settings[keyof Settings]>
  ) => {
    setIsLoading(true);
    try {
      // Here you would typically make an API call to save the settings
      // For now, we'll just update the local state
      setSettings(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          ...values,
        },
      }));

      toast({
        title: "Paramètres mis à jour",
        description: "Les modifications ont été enregistrées avec succès.",
      });
    } catch (error) {
      console.error('Error updating settings:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour des paramètres.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings, isLoading }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};